import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormControl, ReactiveFormsModule, ValidatorFn, AsyncValidatorFn } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SimpleButtonComponent, SimpleInputComponent, TitleContentComponent, CardAccordionComponent, CustomerItemComponent } from 'ngx-zcs-components';
import { AHscanDevicesEnum, AzzurroHubDataType, GetLoggerDataQuery, CmdAzzurroHubService } from 'ngx-core-services';

import { DeviceSerialValidatorPipe } from '../pipes/errors.pipe';
import { azzurroHubDataQuery } from './query-constructor.query';
import { SystemDefinition } from '../../store';

// Interface per dispositivi rilevati dall'Hub
export interface DetectedDevice {
  serial: string;
  type: 'inverter' | 'meter' | 'evChargerCAN' | 'evCharger485' | 'pwrMagic' | 'heatPump';
  online: boolean;
}

// Interface per dispositivi aggiunti alla pianta
export interface AddedDevice {
  serial: string;
  id: string; // UUID generato automaticamente
  dismiss?: 'Uninstall' | 'Delete' | null; // For device lifecycle management
}

const DEFAULT_LABELS = {
  addDevice: 'Add Device',
  addedDevice: 'Added Device',
  detectedDevices: 'Devices Detected by Hub',
  detectedDevicesDescription: 'These devices were automatically detected by your Azzurro Hub.',
  manualDeviceList: 'Manual Device Entry',
  manualDeviceListDescription: 'Manually enter device serial numbers.',
  manualDeviceListPlaceholder: 'E.g. ZM24D12345678',
  noDetectedDevices: 'No devices detected',
  noAddedDevices: 'No devices added',
  noAddedDevicesDescription: 'No devices have been added to this plant yet. Only inverters, meters, and Power Magic devices appear in this list. EMS devices are not shown here.',
  startRescan: 'Start Re-scan',
  refreshDevices: 'Refresh',
  deviceNotExist: 'Device does not exist',
  deviceAlreadyOnACertifiedPlant: 'Device already on a certified plant',
  serialNotValid: 'Serial not valid',
  validationError: 'Validation error',
  uninstall: 'Uninstall',
  delete: 'Delete',
  undo: 'Undo',
  advanced: 'Advanced',
  advancedDescription: 'Manual device entry and advanced options',
  azzurroHubOffline: 'Azzurro Hub offline',
  devicesAddedToAdvancedPlant: 'Devices Added to Advanced Plant',
  clickToAddToYourPlant: 'Click to add them to your plant',
  scanning: 'Scanning...',
  listFoundDevices: 'List Found Devices',
  deviceListContent: 'Device List'
};

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    SimpleButtonComponent,
    SimpleInputComponent,
    TitleContentComponent,
    CardAccordionComponent,
    CustomerItemComponent,
    DeviceSerialValidatorPipe
  ],
  selector: 'customer-azzurro-hub-devices-section',
  templateUrl: './azzurro-hub-devices-section.component.html',
  styleUrls: ['./azzurro-hub-devices-section.component.scss']
})
export class AzzurroHubDevicesSectionComponent implements OnInit, OnDestroy {
  @Input() set azzurroHubSerial(value: string | null) {
    const previousValue = this._azzurroHubSerial;
    this._azzurroHubSerial = value;

    // Se il valore è cambiato e non è vuoto, carica i dispositivi
    if (value && value !== previousValue) {
      this.loadDetectedDevices();
    }
  }

  get azzurroHubSerial(): string | null {
    return this._azzurroHubSerial;
  }

  private _azzurroHubSerial: string | null = null;
  @Input() deviceValidators: {
    syncValidators?: ValidatorFn[];
    asyncValidators?: AsyncValidatorFn[]
  } = { syncValidators: [], asyncValidators: [] };
  @Input() labels: Partial<typeof DEFAULT_LABELS> = {};
  private _existingDevices: string[] | {serial: string, dismiss?: 'Uninstall' | 'Delete' | null}[] = [];
  private _isInitializing = false;

  @Input() set existingDevices(devices: string[] | {serial: string, dismiss?: 'Uninstall' | 'Delete' | null}[]) {
    console.log('🎭 Component received existingDevices:', devices);
    console.log('🎭 Previous _existingDevices:', this._existingDevices);
    console.log('🎭 _isInitializing:', this._isInitializing);

    // Only initialize if devices have actually changed and we're not already initializing
    if (!this._isInitializing && JSON.stringify(devices) !== JSON.stringify(this._existingDevices)) {
      console.log('✅ Devices changed, initializing...');
      this._existingDevices = devices;
      this.initializeExistingDevices(devices);
    } else {
      console.log('❌ Skipping initialization - devices unchanged or already initializing');
    }
  }
  @Input() isVerifiedMode = false; // True for AWS certified plants, false for creation mode
  @Input() emitCompleteData = false; // True to emit complete data, false for backward compatibility
  @Input() system?: SystemDefinition;

  @Output() devicesChange = new EventEmitter<any>(); // Can be string[] or object[] based on emitCompleteData

  // State Variables (MAXIMUM 3)
  // 1. Dispositivi rilevati dall'Hub
  detectedDevices: DetectedDevice[] = [];

  // 2. Dispositivi aggiunti alla pianta
  addedDevices: AddedDevice[] = [];

  // Lista dei dispositivi detected con errori di validazione (per UI feedback)
  detectedDevicesWithErrors: Array<{ device: DetectedDevice; errors: any }> = [];

  // Lista dei dispositivi esplicitamente rimossi dall'utente (per evitare re-aggiunta automatica)
  explicitlyRemovedDevices: Set<string> = new Set();

  // Stato dell'accordion Advanced
  isAdvancedExpanded: boolean = false;

  // 3. Stato generale del componente
  componentState = {
    rescanStatus: AHscanDevicesEnum.idle, // Verrà aggiornato dalla getLoggerData
    loading: false,
    inputSerial: new FormControl('') // Verrà inizializzato in ngOnInit con i validatori
  };

  // Polling durante scanning
  private pollingInterval: any = null;
  private readonly POLLING_INTERVAL_MS = 10000; // 10 secondi

  // Getter for enum access in template
  get AHscanDevicesEnum() {
    return AHscanDevicesEnum;
  }

  // Merged labels
  get mergedLabels() {
    return { ...DEFAULT_LABELS, ...this.labels };
  }

  get aHIsOffline(): boolean {
    return this.system?.metadata?.isConnected === false;
  }

  // Arrays ordinati - mantengono l'ordinamento
  private _sortedDetectedDevices: DetectedDevice[] = [];
  private _sortedAddedDevices: AddedDevice[] = [];

  // Getters per dispositivi ordinati
  get sortedDetectedDevices(): DetectedDevice[] {
    return this._sortedDetectedDevices;
  }

  get sortedAddedDevices(): AddedDevice[] {
    return this._sortedAddedDevices;
  }

  constructor(
    private readonly getLoggerDataQuery: GetLoggerDataQuery,
    private readonly cmdAzzurroHubService: CmdAzzurroHubService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.componentState.inputSerial = new FormControl(
      '',
      this.deviceValidators.syncValidators,
      this.deviceValidators.asyncValidators
    );
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  async addDeviceFromInput(): Promise<void> {
    const serial = this.componentState.inputSerial.value?.trim();
    if (!serial || this.componentState.inputSerial.invalid) {
      return;
    }

    if (this.isDeviceAdded(serial)) {
      return;
    }

    const newDevice: AddedDevice = {
      serial: serial.toUpperCase(),
      id: this.generateDeviceId(),
      dismiss: null
    };

    this.addedDevices.push(newDevice);
    this.clearInputField();

    // Riordina i dispositivi aggiunti
    this.sortAddedDevices();

    this.emitDevicesChange();

    // Forza il change detection per aggiornare la UI
    this.cdr.detectChanges();
  }

  async addDetectedDevice(device: DetectedDevice): Promise<void> {
    if (this.isDeviceAdded(device.serial)) {
      return;
    }

    // Crea un FormControl temporaneo con tutti i validatori per testare il dispositivo
    const tempControl = new FormControl(
      device.serial,
      this.deviceValidators.syncValidators,
      this.deviceValidators.asyncValidators
    );

    // Esegui validazione sincrona
    tempControl.updateValueAndValidity();

    // Se ci sono errori sincroni, mostra errore senza aggiungere
    if (tempControl.errors) {
      console.warn('Device validation failed (sync):', device.serial, tempControl.errors);
      this.addDeviceToErrorList(device, tempControl.errors);
      return;
    }

    // Se ci sono validatori asincroni, aspetta il risultato
    if (this.deviceValidators.asyncValidators && this.deviceValidators.asyncValidators.length > 0) {
      try {
        // Aspetta la validazione asincrona
        await new Promise((resolve, reject) => {
          const subscription = tempControl.statusChanges.subscribe(status => {
            if (status !== 'PENDING') {
              subscription.unsubscribe();
              if (tempControl.errors) {
                reject(tempControl.errors);
              } else {
                resolve(null);
              }
            }
          });
        });
      } catch (errors) {
        console.warn('Device validation failed (async):', device.serial, errors);
        this.addDeviceToErrorList(device, errors);
        return;
      }
    }

    // Se la validazione è passata, aggiungi il dispositivo
    const newDevice: AddedDevice = {
      serial: device.serial,
      id: this.generateDeviceId(),
      dismiss: null
    };

    // Rimuovi dalla lista dei dispositivi esplicitamente rimossi se presente
    this.explicitlyRemovedDevices.delete(device.serial);

    // Rimuovi eventuali errori precedenti per questo dispositivo
    this.detectedDevicesWithErrors = this.detectedDevicesWithErrors.filter(
      entry => entry.device.serial !== device.serial
    );

    console.log(`✅ Device ${device.serial} manually added, cleaned up from error and removed lists`);

    this.addedDevices.push(newDevice);

    // Riordina i dispositivi aggiunti e quelli rilevati (potrebbero avere errori aggiornati)
    this.sortAddedDevices();
    this.sortDetectedDevices();

    this.emitDevicesChange();

    // Forza il change detection per aggiornare la UI
    this.cdr.detectChanges();
  }

  removeDevice(deviceId: string): void {
    const deviceToRemove = this.addedDevices.find(device => device.id === deviceId);
    if (deviceToRemove) {
      // Aggiungi alla lista dei dispositivi esplicitamente rimossi
      this.explicitlyRemovedDevices.add(deviceToRemove.serial);
      console.log(`🚫 Device ${deviceToRemove.serial} explicitly removed by user`);
    }

    this.addedDevices = this.addedDevices.filter(device => device.id !== deviceId);

    // Riordina i dispositivi aggiunti
    this.sortAddedDevices();

    this.emitDevicesChange();

    // Forza il change detection per aggiornare la UI
    this.cdr.detectChanges();
  }

  dismissDevice(deviceId: string, action?: 'Uninstall' | 'Delete'): void {
    const device = this.addedDevices.find(d => d.id === deviceId);
    if (!device) return;

    if (action) {
      device.dismiss = action;
    } else {
      // Toggle dismiss state (undo)
      device.dismiss = null;
    }

    // Riordina i dispositivi aggiunti
    this.sortAddedDevices();

    this.emitDevicesChange();
    this.cdr.detectChanges();
  }

  async startRescan(): Promise<void> {
    if (!this.azzurroHubSerial) return;

    this.componentState.loading = true;

    try {
      await this.cmdAzzurroHubService.rescanDevices(
        this.azzurroHubSerial,
        'Starting device re-scan...'
      );
      // Reset della lista dei dispositivi esplicitamente rimossi durante rescan
      // Solo per sistemi non verificati, per permettere auto-aggiunta dopo rescan
      if (!this.isVerifiedMode) {
        console.log('🔄 Rescan initiated (non-verified mode): clearing explicitly removed devices list');
        this.explicitlyRemovedDevices.clear();
      } else {
        console.log('🔄 Rescan initiated (verified mode): maintaining user preferences');
      }

      // Non impostare lo stato manualmente, verrà aggiornato dalla getLoggerData
      // Wait a bit then refresh to check the status
      setTimeout(() => {
        this.refreshDevices();
      }, 2000);
    } catch (error) {
      console.error('Error starting rescan:', error);
    } finally {
      this.componentState.loading = false;
    }
  }

  async refreshDevices(): Promise<void> {
    if (!this.azzurroHubSerial) return;

    this.componentState.loading = true;
    await this.loadDetectedDevices();
    this.componentState.loading = false;

    // Forza il change detection per aggiornare la UI dopo il refresh
    this.cdr.detectChanges();
  }

  isDeviceAdded(serial: string): boolean {
    return this.addedDevices.some(device => device.serial.toLowerCase() === serial.toLowerCase());
  }

  hasValidationError(serial: string): boolean {
    return this.detectedDevicesWithErrors.some(entry => entry.device.serial === serial);
  }

  getDeviceValidationError(serial: string): string {
    const errorEntry = this.detectedDevicesWithErrors.find(entry => entry.device.serial === serial);
    if (!errorEntry) return '';
    return this.getValidationErrorMessage(errorEntry.errors);
  }

  getValidationErrorMessage(errors: any): string {
    if (!errors) return '';

    if (errors.serialNotValid) {
      return this.mergedLabels.serialNotValid;
    }
    if (errors.deviceNotExist) {
      return this.mergedLabels.deviceNotExist;
    }
    if (errors.deviceAlreadyOnACertifiedPlant) {
      return this.mergedLabels.deviceAlreadyOnACertifiedPlant;
    }

    return this.mergedLabels.validationError;
  }

  // Private Methods (3 methods max)
  private async loadDetectedDevices(): Promise<void> {
    if (!this.azzurroHubSerial) return;

    // Pulisci errori precedenti quando ricarichi i dispositivi
    this.detectedDevicesWithErrors = [];

    console.log('🔄 Loading detected devices from Azzurro Hub:', this.azzurroHubSerial);

    try {
      const networkOnly = true;
      const results = await this.getLoggerDataQuery.execute(
        azzurroHubDataQuery,
        { id: this.azzurroHubSerial },
        networkOnly
      );

      const data = results.data.getLoggerData as AzzurroHubDataType;

      // Update rescan status and manage polling
      if (data?.config?.scanDevices) {
        const previousStatus = this.componentState.rescanStatus;
        this.componentState.rescanStatus = data.config.scanDevices;
        // Gestisci il polling in base al cambio di stato
        this.managePolling(previousStatus, this.componentState.rescanStatus);
      }

      // Extract detected devices
      this.detectedDevices = [
        ...data?.inverter ?? [],
        ...data?.pwrMagic ?? [],
      ].map(device => ({
        serial: device.sn,
        type: device.deviceType as DetectedDevice['type'],
        online: device.connected,
      }));

    } catch (error) {
      console.error('Error loading detected devices:', error);
      this.detectedDevices = [];
    }

    // Auto-aggiungi dispositivi rilevati che passano la validazione
    await this.autoAddValidDetectedDevices();

    // Ordina i dispositivi dopo averli caricati
    this.sortDetectedDevices();
    this.sortAddedDevices();

    // Forza il change detection dopo aver caricato i dispositivi
    this.cdr.detectChanges();
  }

  private clearInputField(): void {
    this.componentState.inputSerial.setValue('');
    this.componentState.inputSerial.markAsUntouched();
  }

  private generateDeviceId(): string {
    return 'device_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  private sortDetectedDevices(): void {
    this._sortedDetectedDevices = [...this.detectedDevices].sort((a, b) => {
      const aHasError = this.hasValidationError(a.serial);
      const bHasError = this.hasValidationError(b.serial);

      // Prima i dispositivi senza errori, poi quelli con errori
      if (aHasError && !bHasError) return 1;
      if (!aHasError && bHasError) return -1;

      // All'interno dello stesso gruppo, ordina alfabeticamente per serial
      return a.serial.localeCompare(b.serial, undefined, { numeric: true, sensitivity: 'base' });
    });
  }

  private sortAddedDevices(): void {
    this._sortedAddedDevices = [...this.addedDevices].sort((a, b) => {
      return a.serial.localeCompare(b.serial, undefined, { numeric: true, sensitivity: 'base' });
    });
  }

  private emitDevicesChange(): void {
    if (this.emitCompleteData) {
      // Emit complete device data including dismiss state
      const deviceData = this.addedDevices.map(device => ({
        serial: device.serial,
        dismiss: device.dismiss
      }));
      this.devicesChange.emit(deviceData);
    } else {
      // Backward compatibility: emit only serials of active devices
      const activeDeviceSerials = this.addedDevices
        .filter(device => !device.dismiss)
        .map(device => device.serial);
      this.devicesChange.emit(activeDeviceSerials);
    }
  }

  private addDeviceToErrorList(device: DetectedDevice, errors: any): void {
    // Rimuovi eventuali errori precedenti per questo dispositivo
    this.detectedDevicesWithErrors = this.detectedDevicesWithErrors.filter(
      entry => entry.device.serial !== device.serial
    );

    // Aggiungi il nuovo errore
    this.detectedDevicesWithErrors.push({ device, errors });

    // Riordina i dispositivi rilevati perché gli errori potrebbero aver cambiato l'ordine
    this.sortDetectedDevices();

    this.cdr.detectChanges();
  }

  /**
   * Auto-aggiungi dispositivi rilevati che passano la validazione
   * Solo per sistemi non verificati (isVerifiedMode === false)
   * Mantiene il meccanismo esistente per gestione manuale
   */
  private async autoAddValidDetectedDevices(): Promise<void> {
    // Auto-aggiunta solo per sistemi non verificati (Firebase/legacy)
    if (this.isVerifiedMode) {
      console.log('🔒 Verified mode: skipping auto-add, manual control required');
      return;
    }

    if (this.detectedDevices.length === 0) {
      console.log('🤖 No detected devices to auto-add');
      return;
    }

    console.log('🤖 Starting auto-add for', this.detectedDevices.length, 'detected devices (non-verified mode)');

    for (const device of this.detectedDevices) {
      // Salta se il dispositivo è già stato aggiunto
      if (this.isDeviceAdded(device.serial)) {
        console.log(`⏭️ Device ${device.serial} already added, skipping`);
        continue;
      }

      // Salta se il dispositivo è stato esplicitamente rimosso dall'utente
      if (this.explicitlyRemovedDevices.has(device.serial)) {
        console.log(`🚫 Device ${device.serial} was explicitly removed by user, skipping auto-add`);
        continue;
      }

      // Salta se il dispositivo è offline (solo dispositivi online)
      if (!device.online) {
        console.log(`⚠️ Device ${device.serial} is offline, skipping auto-add`);
        continue;
      }

      console.log(`🔍 Validating device ${device.serial} for auto-add`);

      // Testa la validazione del dispositivo usando la stessa logica di addDetectedDevice
      const tempControl = new FormControl(
        device.serial,
        this.deviceValidators.syncValidators,
        this.deviceValidators.asyncValidators
      );

      // Esegui validazione sincrona
      tempControl.updateValueAndValidity();

      // Se ci sono errori sincroni, aggiungi alla lista errori
      if (tempControl.errors) {
        console.warn(`❌ Device ${device.serial} failed sync validation:`, tempControl.errors);
        this.addDeviceToErrorList(device, tempControl.errors);
        continue;
      }

      // Se ci sono validatori asincroni, aspetta il risultato
      if (this.deviceValidators.asyncValidators && this.deviceValidators.asyncValidators.length > 0) {
        try {
          await new Promise((resolve, reject) => {
            const subscription = tempControl.statusChanges.subscribe(status => {
              if (status !== 'PENDING') {
                subscription.unsubscribe();
                if (tempControl.errors) {
                  reject(tempControl.errors);
                } else {
                  resolve(null);
                }
              }
            });
          });
        } catch (errors) {
          console.warn(`❌ Device ${device.serial} failed async validation:`, errors);
          this.addDeviceToErrorList(device, errors);
          continue;
        }
      }

      // Se la validazione è passata, aggiungi automaticamente il dispositivo
      console.log(`✅ Device ${device.serial} validated successfully, auto-adding`);

      // Rimuovi eventuali errori precedenti per questo dispositivo
      this.detectedDevicesWithErrors = this.detectedDevicesWithErrors.filter(
        entry => entry.device.serial !== device.serial
      );

      const newDevice: AddedDevice = {
        serial: device.serial,
        id: this.generateDeviceId(),
        dismiss: null
      };

      this.addedDevices.push(newDevice);
    }

    // Conta quanti dispositivi sono stati aggiunti durante questo processo
    const autoAddedCount = this.addedDevices.filter(device =>
      this.detectedDevices.some(detected => detected.serial === device.serial)
    ).length;

    // Emetti i cambiamenti se ci sono stati dispositivi aggiunti automaticamente
    if (autoAddedCount > 0) {
      console.log(`🎉 Auto-added ${autoAddedCount} devices successfully`);
      // Riordina dopo aver aggiunto dispositivi automaticamente
      this.sortAddedDevices();
      this.emitDevicesChange();
    } else {
      console.log('📋 No devices were auto-added this time');
    }
  }

  // Polling management methods
  private startPolling(): void {
    if (this.pollingInterval) {
      return; // Already polling
    }

    console.log('🔄 Starting automatic polling every', this.POLLING_INTERVAL_MS / 1000, 'seconds during scanning');
    this.pollingInterval = setInterval(async () => {
      if (this.componentState.rescanStatus !== AHscanDevicesEnum.idle) {
        console.log('⏰ Automatic polling refresh during scanning');
        await this.refreshDevices();
      } else {
        // Stop polling if status changed to idle
        console.log('✋ Stopping polling - scan completed');
        this.stopPolling();
      }
    }, this.POLLING_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollingInterval) {
      console.log('🛑 Stopping automatic polling');
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  private managePolling(previousStatus: AHscanDevicesEnum, currentStatus: AHscanDevicesEnum): void {
    const wasScanning = previousStatus !== AHscanDevicesEnum.idle;
    const isScanning = currentStatus !== AHscanDevicesEnum.idle;

    if (!wasScanning && isScanning) {
      // Started scanning - start polling
      console.log('🚀 Scan started - beginning automatic polling');
      this.startPolling();
    } else if (wasScanning && !isScanning) {
      // Stopped scanning - stop polling
      console.log('🏁 Scan completed - stopping automatic polling');
      this.stopPolling();
    }
  }

  // Template helper methods
  getRescanRefreshAction = () => {
    return this.componentState.rescanStatus === AHscanDevicesEnum.idle ?
      () => this.startRescan() :
      () => this.refreshDevices();
  };

  getStartRescanAction = () => () => this.startRescan();

  getAddDetectedDeviceAction = (device: DetectedDevice) => () => this.addDetectedDevice(device);
  getAddDeviceFromInputAction = () => () => this.addDeviceFromInput();
  getRemoveDeviceAction = (deviceId: string) => () => this.removeDevice(deviceId);
  getDismissDeviceAction = (deviceId: string, action?: 'Uninstall' | 'Delete') => () => this.dismissDevice(deviceId, action);

  toggleAdvancedSection(): void {
    this.isAdvancedExpanded = !this.isAdvancedExpanded;
    this.cdr.detectChanges();
  }

  private initializeExistingDevices(devices: string[] | {serial: string, dismiss?: 'Uninstall' | 'Delete' | null}[]): void {
    console.log('🚀 initializeExistingDevices called with:', devices);

    this._isInitializing = true;
    console.log('🔒 _isInitializing set to true');

    // Clear existing devices first to avoid duplicates
    console.log('🧹 Clearing addedDevices. Previous:', this.addedDevices);
    this.addedDevices = [];

    // Add each existing device to the addedDevices array
    devices.forEach((deviceInput, index) => {
      console.log(`🔄 Processing device ${index}:`, deviceInput);

      if (typeof deviceInput === 'string') {
        // Backward compatibility: string array
        if (deviceInput && deviceInput.trim() !== '') {
          const device: AddedDevice = {
            serial: deviceInput.toUpperCase(),
            id: this.generateDeviceId(),
            dismiss: null
          };
          this.addedDevices.push(device);
          console.log(`  ✅ Added string device:`, device);
        } else {
          console.log(`  ❌ Skipped empty string device`);
        }
      } else {
        // New format: object with serial and dismiss state
        if (deviceInput.serial && deviceInput.serial.trim() !== '') {
          const device: AddedDevice = {
            serial: deviceInput.serial.toUpperCase(),
            id: this.generateDeviceId(),
            dismiss: deviceInput.dismiss || null
          };
          this.addedDevices.push(device);
          console.log(`  ✅ Added object device:`, device);
        } else {
          console.log(`  ❌ Skipped device with empty serial:`, deviceInput);
        }
      }
    });

    console.log('📋 Final addedDevices after initialization:', this.addedDevices);

    // Ordina i dispositivi aggiunti dopo l'inizializzazione
    this.sortAddedDevices();

    // Emit the devices change event to sync with parent
    console.log('📡 Emitting devices change...');
    this.emitDevicesChange();

    // Force change detection to update the UI
    console.log('🔄 Triggering change detection...');
    this.cdr.detectChanges();

    this._isInitializing = false;
    console.log('🔓 _isInitializing set to false');
    console.log('✅ initializeExistingDevices completed');
  }
}
